import { useRef, useState } from 'react'
import { motion } from 'motion/react'
import { ArrowDown } from 'lucide-react'
import { useLocale } from '../context/LocaleContext'
import { useAvatarScrub } from '../hooks/useAvatarScrub'
import { useMediaQuery } from '../hooks/useMediaQuery'
import { useMobileHeroSequence } from '../hooks/useMobileHeroSequence'
import type { Experience, ExperienceId } from '../content/types'
import { ConstellationNode } from './ConstellationNode'

interface HeroProps {
  activeId: string
  onNavigate: (id: string) => void
}

const heroExperienceIds = ['korpu', 'abona', 'kivi', 'ozon', 'onetrak'] as const satisfies readonly ExperienceId[]
type HeroExperienceId = (typeof heroExperienceIds)[number]

const desktopPositionById: Record<HeroExperienceId, string> = {
  korpu: 'hero-left-1',
  abona: 'hero-left-2',
  kivi: 'hero-left-3',
  ozon: 'hero-right-1',
  onetrak: 'hero-right-2',
}

const mobileLeftIds = ['korpu', 'abona', 'kivi'] as const satisfies readonly HeroExperienceId[]
const mobileRightIds = ['ozon', 'onetrak'] as const satisfies readonly HeroExperienceId[]

export function Hero({ activeId, onNavigate }: HeroProps) {
  const { content } = useLocale()
  const { ui, experiences, educationNodeAnimation } = content
  const heroRef = useRef<HTMLElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const [videoReady, setVideoReady] = useState(false)
  const [videoFailed, setVideoFailed] = useState(false)
  const largeLayout = useMediaQuery('(min-width: 768px)')
  const touchDevice = useMediaQuery('(hover: none), (pointer: coarse), (any-pointer: coarse)')
  const fineDesktop = useMediaQuery('(min-width: 768px) and (pointer: fine)')
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)')
  const mobileHeroLayout = !largeLayout || touchDevice
  const interactive = !mobileHeroLayout && fineDesktop && !reducedMotion && !videoFailed
  const mobileSequenceEnabled = mobileHeroLayout && !videoFailed
  const heroExperiences = heroExperienceIds
    .map((id) => experiences.find((experience) => experience.id === id))
    .filter((experience): experience is Experience => Boolean(experience))
  const experienceById = new Map(heroExperiences.map((experience) => [experience.id, experience]))

  useAvatarScrub({ videoRef, targetRef: heroRef, enabled: interactive })
  const mobilePhase = useMobileHeroSequence({ videoRef, targetRef: heroRef, enabled: mobileSequenceEnabled })
  const leftAnimationsActive = mobilePhase === 'left'
  const rightAnimationsActive = mobilePhase === 'right'

  const avatar = (
    <motion.div className="avatar-stage" initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.12 }}>
      <picture className="avatar-poster">
        <source srcSet="/media/avatar-poster.avif" type="image/avif" />
        <source srcSet="/media/avatar-poster.webp" type="image/webp" />
        <img src="/media/avatar-poster.jpg" alt="Futuristic portrait of Anton Kogan wearing augmented-reality glasses" width="1376" height="768" fetchPriority="high" />
      </picture>
      {(interactive || mobileSequenceEnabled) && (
        <video
          ref={videoRef}
          className={videoReady ? 'avatar-video ready' : 'avatar-video'}
          muted
          playsInline
          preload="auto"
          poster="/media/avatar-poster.jpg"
          onCanPlay={() => setVideoReady(true)}
          onError={() => {
            setVideoReady(false)
            setVideoFailed(true)
          }}
          aria-label={ui.interactivePortrait}
        >
          <source src="/media/avatar-scrub.mp4" type="video/mp4" />
        </video>
      )}
    </motion.div>
  )

  return (
    <section className={`hero ${mobileHeroLayout ? 'mobile-sequence-hero' : ''}`} id="top" ref={heroRef} aria-labelledby="hero-title">
      <div className="hero-copy">
        {ui.heroEyebrow && <motion.p className="eyebrow" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>{ui.heroEyebrow}</motion.p>}
        <motion.h1 id="hero-title" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }}>{ui.heroTitle}</motion.h1>
        <motion.p className="hero-intro" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.14 }}>{ui.heroIntro}</motion.p>
      </div>

      <svg className="constellation-lines" viewBox="0 0 1440 900" aria-hidden="true" preserveAspectRatio="none">
        <g><path d="M720 440 160 190M720 440 1280 190M720 440 140 445M720 440 1300 445M720 440 180 700M720 440 1260 700" /></g>
      </svg>

      {!mobileHeroLayout && (
        <div className="desktop-constellation" aria-label={`${ui.exploreWork} / ${ui.exploreEducation}`}>
          {heroExperiences.map((experience) => (
            <ConstellationNode key={experience.id} label={experience.nodeLabel} icon={experience.icon} animation={experience.nodeAnimation} playAnimation={!reducedMotion} active={activeId === `experience-${experience.id}`} positionClass={desktopPositionById[experience.id as HeroExperienceId]} onClick={() => onNavigate(`experience-${experience.id}`)} />
          ))}
          <ConstellationNode label={ui.exploreEducation} icon="school" animation={educationNodeAnimation} playAnimation={!reducedMotion} active={activeId.startsWith('education-')} positionClass="hero-right-3" onClick={() => onNavigate('education')} />
        </div>
      )}

      {!mobileHeroLayout && avatar}

      {mobileHeroLayout && (
        <div className="mobile-hero-stage" data-mobile-phase={mobilePhase} aria-label={`${ui.exploreWork} / ${ui.exploreEducation}`}>
          <div className="mobile-node-column mobile-node-column-left">
            {mobileLeftIds.map((id) => {
              const experience = experienceById.get(id)
              if (!experience) return null
              return <ConstellationNode key={id} label={experience.nodeLabel} icon={experience.icon} animation={experience.nodeAnimation} animationActive={leftAnimationsActive} active={activeId === `experience-${id}`} onClick={() => onNavigate(`experience-${id}`)} />
            })}
          </div>
          {avatar}
          <div className="mobile-node-column mobile-node-column-right">
            {mobileRightIds.map((id) => {
              const experience = experienceById.get(id)
              if (!experience) return null
              return <ConstellationNode key={id} label={experience.nodeLabel} icon={experience.icon} animation={experience.nodeAnimation} animationActive={rightAnimationsActive} active={activeId === `experience-${id}`} onClick={() => onNavigate(`experience-${id}`)} />
            })}
            <ConstellationNode label={ui.exploreEducation} icon="school" animation={educationNodeAnimation} animationActive={rightAnimationsActive} active={activeId.startsWith('education-')} onClick={() => onNavigate('education')} />
          </div>
        </div>
      )}

      <button className="hero-scroll" onClick={() => onNavigate('impact')} aria-label={ui.workTitle}><ArrowDown size={18} /></button>
    </section>
  )
}
